import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { exportToExcel, exportMultipleToExcel } from '../utils/exportToExcel';
import { reportsAPI } from '../services/api';
import useApi from '../hooks/useApi';
import './Reports.css';

const Reports = () => {
  const [reportType, setReportType] = useState('bookings');
  const [dateRange, setDateRange] = useState({ start: '2025-10-01', end: '2025-10-31' });
  const [period, setPeriod] = useState(30); // For visitor analytics
  const [interval, setInterval] = useState('day'); // For visitor trends
  const [reportData, setReportData] = useState({
    bookingsByCountry: [],
    revenueByHotel: [],
    bookingsOverTime: [],
    userDemographics: [],
    visitorAnalytics: null,
    visitorTrends: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch report data based on selected report type and date range
  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = dateRange;
      console.log('Fetching report data for:', reportType, 'with date range:', start, 'to', end);
      
      switch (reportType) {
        case 'revenue':
          try {
            console.log('Calling getRevenueByHotel API');
            const revenueRes = await reportsAPI.getRevenueByHotel(start, end);
            console.log('Revenue API response:', revenueRes);
            // Handle both possible response formats
            const revenueData = revenueRes.data?.data || revenueRes.data || [];
            setReportData(prev => ({ ...prev, revenueByHotel: Array.isArray(revenueData) ? revenueData : [] }));
          } catch (err) {
            console.error('Error fetching revenue data:', err);
            setError(new Error(`Revenue Error: ${err.response?.data?.message || err.message}`));
          }
          break;
        case 'trends':
          try {
            console.log('Calling getBookingTrends API');
            const trendsRes = await reportsAPI.getBookingTrends(start, end);
            console.log('Trends API response:', trendsRes);
            // Handle both possible response formats
            const trendsData = trendsRes.data?.data || trendsRes.data || [];
            setReportData(prev => ({ ...prev, bookingsOverTime: Array.isArray(trendsData) ? trendsData : [] }));
          } catch (err) {
            console.error('Error fetching trends data:', err);
            setError(new Error(`Trends Error: ${err.response?.data?.message || err.message}`));
          }
          break;
        case 'demographics':
          try {
            console.log('Calling getUserDemographics API');
            const demographicsRes = await reportsAPI.getUserDemographics(start, end);
            console.log('Demographics API response:', demographicsRes);
            // Handle both possible response formats
            const demographicsData = demographicsRes.data?.data || demographicsRes.data || [];
            setReportData(prev => ({ ...prev, userDemographics: Array.isArray(demographicsData) ? demographicsData : [] }));
          } catch (err) {
            console.error('Error fetching demographics data:', err);
            setError(new Error(`Demographics Error: ${err.response?.data?.message || err.message}`));
          }
          break;
        case 'visitor-analytics':
          try {
            console.log('Calling getVisitorAnalytics API with params:', { period, startDate: start, endDate: end });
            const visitorRes = await reportsAPI.getVisitorAnalytics({ period, startDate: start, endDate: end });
            console.log('Visitor Analytics API response:', visitorRes);
            setReportData(prev => ({ ...prev, visitorAnalytics: visitorRes.data?.data || visitorRes.data || null }));
          } catch (visitorErr) {
            console.error('Error fetching visitor analytics:', visitorErr);
            setError(new Error(`Visitor Analytics Error: ${visitorErr.response?.data?.message || visitorErr.message}`));
          }
          break;
        case 'visitor-trends':
          try {
            console.log('Calling getVisitorTrends API with params:', { period, startDate: start, endDate: end, interval });
            const trendRes = await reportsAPI.getVisitorTrends({ period, startDate: start, endDate: end, interval });
            console.log('Visitor Trends API response:', trendRes);
            const trendsData = trendRes.data?.data || trendRes.data || [];
            setReportData(prev => ({ ...prev, visitorTrends: Array.isArray(trendsData) ? trendsData : [] }));
          } catch (trendErr) {
            console.error('Error fetching visitor trends:', trendErr);
            setError(new Error(`Visitor Trends Error: ${trendErr.response?.data?.message || trendErr.message}`));
          }
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(new Error(`Report Error: ${err.response?.data?.message || err.message || 'An unknown error occurred'}`));
    } finally {
      setLoading(false);
    }
  };

  // Add a useEffect to trigger fetch when component mounts or when report type changes
  useEffect(() => {
    // Add event listener for manual refetch
    const handleRefetch = () => fetchReportData();
    window.addEventListener('fetchReportData', handleRefetch);
    
    // Initial fetch
    fetchReportData();
    
    return () => {
      window.removeEventListener('fetchReportData', handleRefetch);
    };
  }, [reportType, dateRange, period, interval]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

  // Export functions
  const exportReportToExcel = () => {
    switch (reportType) {
      case 'revenue':
        exportToExcel(reportData.revenueByHotel, 'revenue_by_hotel', 'Revenue by Hotel');
        break;
      case 'trends':
        exportToExcel(reportData.bookingsOverTime, 'booking_trends', 'Booking Trends');
        break;
      case 'demographics':
        exportToExcel(reportData.userDemographics, 'user_demographics', 'User Demographics');
        break;
      case 'visitor-analytics':
        // Export visitor analytics data
        const visitorData = [];
        if (reportData.visitorAnalytics?.overview) {
          visitorData.push({ section: 'Overview', ...reportData.visitorAnalytics.overview });
        }
        if (reportData.visitorAnalytics?.device_stats) {
          reportData.visitorAnalytics.device_stats.forEach(item => {
            visitorData.push({ section: 'Device Stats', ...item });
          });
        }
        if (reportData.visitorAnalytics?.browser_stats) {
          reportData.visitorAnalytics.browser_stats.forEach(item => {
            visitorData.push({ section: 'Browser Stats', ...item });
          });
        }
        exportToExcel(visitorData, 'visitor_analytics', 'Visitor Analytics');
        break;
      case 'visitor-trends':
        exportToExcel(reportData.visitorTrends, 'visitor_trends', 'Visitor Trends');
        break;
      default:
        break;
    }
  };

  const exportAllReportsToExcel = async () => {
    setLoading(true);
    try {
      const { start, end } = dateRange;
      
      // Fetch all report data (excluding bookings since it returns 500 error)
      const [revenueRes, trendsRes, demographicsRes] = await Promise.all([
        reportsAPI.getRevenueByHotel(start, end),
        reportsAPI.getBookingTrends(start, end),
        reportsAPI.getUserDemographics(start, end)
      ]);
      
      exportMultipleToExcel([
        { data: revenueRes.data?.data || [], sheetName: 'Revenue by Hotel' },
        { data: trendsRes.data?.data || [], sheetName: 'Booking Trends' },
        { data: demographicsRes.data?.data || [], sheetName: 'User Demographics' }
      ], 'all_reports');
    } catch (err) {
      setError(err);
      console.error('Error exporting all reports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Quick period selectors for visitor analytics
  const setQuickPeriod = (days) => {
    setPeriod(days);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    
    setDateRange({
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    });
  };

  const renderVisitorAnalytics = () => {
    if (!reportData.visitorAnalytics) return null;

    const { overview, device_stats, browser_stats, os_stats, country_stats, path_stats, status_stats } = reportData.visitorAnalytics;

    return (
      <div className="visitor-analytics">
        {/* Overview Cards */}
        <div className="summary-stats">
          <div className="stat-card">
            <h3>Total Visitors</h3>
            <p className="stat-value">{overview?.total_visitors || 0}</p>
          </div>
          
          <div className="stat-card">
            <h3>Recent Visitors</h3>
            <p className="stat-value">{overview?.recent_visitors || 0}</p>
            <p className="stat-desc">Last 24 hours</p>
          </div>
          
          <div className="stat-card">
            <h3>Avg Response Time</h3>
            <p className="stat-value">{overview?.avg_response_time ? `${overview.avg_response_time}ms` : 'N/A'}</p>
          </div>
        </div>

        <div className="chart-grid">
          {/* Device Type Distribution */}
          {device_stats && device_stats.length > 0 && (
            <div className="chart-container">
              <h3>Device Type Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={device_stats}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="device_type"
                    label={({ device_type, percentage }) => `${device_type}: ${percentage}%`}
                  >
                    {device_stats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Browser Usage Statistics */}
          {browser_stats && browser_stats.length > 0 && (
            <div className="chart-container">
              <h3>Browser Usage Statistics</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={browser_stats} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="browser" type="category" scale="band" />
                  <Tooltip formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]} />
                  <Legend />
                  <Bar dataKey="count" fill="#8884d8" name="Visitors">
                    {browser_stats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Operating System Breakdown */}
          {os_stats && os_stats.length > 0 && (
            <div className="chart-container">
              <h3>Operating System Breakdown</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={os_stats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="os" />
                  <YAxis />
                  <Tooltip formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]} />
                  <Legend />
                  <Bar dataKey="count" fill="#82ca9d" name="Visitors" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Geographic Distribution */}
          {country_stats && country_stats.length > 0 && (
            <div className="chart-container">
              <h3>Geographic Distribution</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Visitors</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {country_stats.map((item, index) => (
                      <tr key={index}>
                        <td>{item.country}</td>
                        <td>{item.count}</td>
                        <td>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Popular Pages/Paths */}
          {path_stats && path_stats.length > 0 && (
            <div className="chart-container">
              <h3>Popular Pages/Paths</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Path</th>
                      <th>Visits</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {path_stats.map((item, index) => (
                      <tr key={index}>
                        <td>{item.path}</td>
                        <td>{item.count}</td>
                        <td>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* HTTP Status Codes */}
          {status_stats && status_stats.length > 0 && (
            <div className="chart-container">
              <h3>HTTP Status Codes</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Status Code</th>
                      <th>Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status_stats.map((item, index) => (
                      <tr key={index} className={item.status_code >= 400 ? 'error-status' : ''}>
                        <td>{item.status_code}</td>
                        <td>{item.count}</td>
                        <td>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderVisitorTrends = () => {
    return (
      <div className="report-content">
        <h2>Visitor Trends Over Time</h2>
        <div className="chart-grid">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={reportData.visitorTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="visitors" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                  name="Visitors" 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderReport = () => {
    switch (reportType) {
      case 'revenue':
        // Check if we have data to display
        if (!reportData.revenueByHotel || reportData.revenueByHotel.length === 0) {
          return (
            <div className="report-content">
              <h2>Revenue by Hotel</h2>
              <div className="no-data">No revenue data available for the selected date range.</div>
            </div>
          );
        }
        return (
          <div className="report-content">
            <h2>Revenue by Hotel</h2>
            <div className="chart-grid">
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={reportData.revenueByHotel}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="revenue" fill="#8884d8" name="Revenue ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      case 'trends':
        // Check if we have data to display
        if (!reportData.bookingsOverTime || reportData.bookingsOverTime.length === 0) {
          return (
            <div className="report-content">
              <h2>Booking Trends</h2>
              <div className="no-data">No trend data available for the selected date range.</div>
            </div>
          );
        }
        return (
          <div className="report-content">
            <h2>Booking Trends</h2>
            <div className="chart-grid">
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={reportData.bookingsOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="bookings" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                      name="Bookings" 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      case 'demographics':
        // Check if we have data to display
        if (!reportData.userDemographics || reportData.userDemographics.length === 0) {
          return (
            <div className="report-content">
              <h2>User Demographics</h2>
              <div className="no-data">No demographic data available for the selected date range.</div>
            </div>
          );
        }
        return (
          <div className="report-content">
            <h2>User Demographics</h2>
            <div className="chart-grid">
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={reportData.userDemographics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="users" fill="#82ca9d" name="Users" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      case 'visitor-analytics':
        return renderVisitorAnalytics();
      case 'visitor-trends':
        // Check if we have data to display
        if (!reportData.visitorTrends || reportData.visitorTrends.length === 0) {
          return (
            <div className="report-content">
              <h2>Visitor Trends</h2>
              <div className="no-data">No visitor trend data available for the selected period.</div>
            </div>
          );
        }
        return renderVisitorTrends();
      default:
        return null;
    }
  };

  // Handle loading and error states
  if (loading) {
    return <div className="loading">Loading report data...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <h3>Error loading report data</h3>
        <p>{error.message}</p>
        <div style={{ marginTop: '20px' }}>
          <button className="btn btn-primary" onClick={() => window.location.reload()} style={{ marginRight: '10px' }}>
            Refresh Page
          </button>
          <button className="btn btn-secondary" onClick={fetchReportData}>
            Retry Report
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Reports & Analytics</h1>
        <div>
          <button 
            className="btn btn-primary" 
            onClick={exportReportToExcel}
            style={{ marginRight: '10px' }}
          >
            Export Current Report
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={exportAllReportsToExcel}
          >
            Export All Reports
          </button>
        </div>
      </div>

      <div className="reports-controls">
        <div className="control-group">
          <label>Report Type:</label>
          <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
            <option value="revenue">Revenue by Hotel</option>
            <option value="trends">Booking Trends</option>
            <option value="demographics">User Demographics</option>
            <option value="visitor-analytics">Visitor Analytics</option>
            <option value="visitor-trends">Visitor Trends</option>
          </select>
        </div>
        
        {(reportType === 'visitor-analytics' || reportType === 'visitor-trends') ? (
          <>
            <div className="control-group">
              <label>Period:</label>
              <div className="quick-select-buttons">
                <button 
                  className={`btn ${period === 7 ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setQuickPeriod(7)}
                >
                  Last 7 Days
                </button>
                <button 
                  className={`btn ${period === 30 ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setQuickPeriod(30)}
                >
                  Last 30 Days
                </button>
                <button 
                  className={`btn ${period === 90 ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setQuickPeriod(90)}
                >
                  Last 90 Days
                </button>
              </div>
            </div>
            
            {reportType === 'visitor-trends' && (
              <div className="control-group">
                <label>Interval:</label>
                <select value={interval} onChange={(e) => setInterval(e.target.value)}>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
              </div>
            )}
          </>
        ) : (
          <div className="control-group">
            <label>Date Range:</label>
            <div className="date-range">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              />
              <span>to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              />
            </div>
          </div>
        )}
        
        <button className="btn btn-primary">Generate Report</button>
      </div>

      {renderReport()}

      {/* Summary Statistics */}
      <div className="summary-stats">
        <div className="stat-card">
          <h3>Total Bookings</h3>
          <p className="stat-value">1,242</p>
          <p className="stat-change positive">+12% from last period</p>
        </div>
        
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p className="stat-value">$45,678</p>
          <p className="stat-change positive">+8% from last period</p>
        </div>
        
        <div className="stat-card">
          <h3>Average Booking Value</h3>
          <p className="stat-value">$142</p>
          <p className="stat-change neutral">+2% from last period</p>
        </div>
        
        <div className="stat-card">
          <h3>New Users</h3>
          <p className="stat-value">342</p>
          <p className="stat-change negative">-3% from last period</p>
        </div>
      </div>
    </div>
  );
};

export default Reports;